import React, { FormEvent, useCallback, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';

import Upload from '../../components/forms/Upload';
import { useAuth } from '../../hooks/auth';
import { useToast } from '../../hooks/toast';
import {
  ContentResourceType,
  postContentResource,
  uploadFiles,
} from '../../services/api';
import {
  normalizePodcastEmbedUrl,
  normalizeVideoEmbedUrl,
} from '../../utils/embedUrl';
import { Container, FormCard, Header } from './styles';

const CreateResource: React.FC = () => {
  const history = useHistory();
  const { user } = useAuth();
  const { addToast } = useToast();
  const [type, setType] = useState<ContentResourceType>('video');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [url, setUrl] = useState('');
  const [caption, setCaption] = useState('');
  const [source, setSource] = useState('');
  const [alternativeText, setAlternativeText] = useState('');
  const [width, setWidth] = useState('100%');
  const [height, setHeight] = useState('650px');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleUpload = useCallback(
    async (files: File[]) => {
      if (!user.provider?.id) {
        addToast({
          title: 'Ops ...',
          description: 'Não foi possível identificar o provedor.',
          type: 'error',
        });
        return;
      }

      try {
        setUploading(true);
        const formData = new FormData();
        const isInfographic = type === 'infographic';
        formData.append(isInfographic ? 'infographics' : 'images', files[0]);
        const response = await uploadFiles({
          formData,
          provider_id: user.provider.id,
          type: isInfographic ? 'infographics' : 'images',
        });
        const uploadedUrl = isInfographic
          ? response?.data.url
          : response?.data[0]?.url;
        setUrl(uploadedUrl || '');
      } catch (error) {
        addToast({
          title: 'Ops ...',
          description: 'Não foi possível enviar o arquivo.',
          type: 'error',
        });
      } finally {
        setUploading(false);
      }
    },
    [addToast, type, user.provider],
  );

  const handleSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      let normalizedUrl = url.trim();
      if (type === 'video') {
        normalizedUrl = normalizeVideoEmbedUrl(url) || '';
      }
      if (type === 'podcast') {
        normalizedUrl = normalizePodcastEmbedUrl(url) || '';
      }

      if (!title.trim() || !normalizedUrl) {
        addToast({
          title: 'Ops ...',
          description: 'Informe o título e uma URL válida para o recurso.',
          type: 'error',
        });
        return;
      }

      const payload: Record<string, string> = { url: normalizedUrl };
      if (type === 'infographic') {
        payload.width = width;
        payload.height = height;
      }
      if (type === 'image') {
        payload.caption = caption || title;
        payload.source = source;
        payload.alternativeText = alternativeText;
      }

      try {
        setLoading(true);
        await postContentResource({
          title: title.trim(),
          description: description.trim() || undefined,
          type,
          payload,
        });
        addToast({
          title: 'Sucesso!',
          description: 'Recurso criado e disponível na biblioteca.',
          type: 'success',
        });
        history.push('/dashboard/my-resources');
      } catch (error) {
        addToast({
          title: 'Ops ...',
          description: 'Não foi possível criar o recurso.',
          type: 'error',
        });
      } finally {
        setLoading(false);
      }
    },
    [
      addToast,
      alternativeText,
      caption,
      description,
      height,
      history,
      source,
      title,
      type,
      url,
      width,
    ],
  );

  return (
    <Container className="slideDown">
      <Header>
        <h1>Novo Recurso</h1>
        <div>
          <Link className="secondary" to="/dashboard/my-resources">
            Voltar para recursos
          </Link>
        </div>
      </Header>

      <FormCard onSubmit={handleSubmit}>
        <div className="row">
          <label>
            Tipo do recurso
            <select
              value={type}
              onChange={event => {
                setType(event.target.value as ContentResourceType);
                setUrl('');
              }}
            >
              <option value="video">Videoaula</option>
              <option value="podcast">Podcast</option>
              <option value="infographic">Infográfico</option>
              <option value="image">Imagem</option>
            </select>
          </label>
          <label>
            Título
            <input
              value={title}
              maxLength={255}
              onChange={event => setTitle(event.target.value)}
              placeholder="Título que aparecerá na biblioteca"
            />
          </label>
        </div>

        <label>
          Descrição (opcional)
          <textarea
            value={description}
            maxLength={2000}
            onChange={event => setDescription(event.target.value)}
          />
        </label>

        {(type === 'image' || type === 'infographic') && (
          <div className="upload">
            <Upload
              onUpload={handleUpload}
              loading={uploading}
              formts={
                type === 'image'
                  ? [
                      { extension: '.jpg', types: 'image/jpeg' },
                      { extension: '.png', types: 'image/png' },
                      { extension: '.gif', types: 'image/gif' },
                    ]
                  : [{ extension: '.zip', types: 'application/zip' }]
              }
            />
          </div>
        )}

        <label>
          URL do recurso
          <input
            value={url}
            onChange={event => setUrl(event.target.value)}
            placeholder={
              type === 'video' || type === 'podcast'
                ? 'Cole a URL ou o iframe'
                : 'Envie o arquivo acima ou informe a URL'
            }
          />
        </label>

        {type === 'infographic' && (
          <div className="row">
            <label>
              Largura
              <input
                value={width}
                onChange={event => setWidth(event.target.value)}
              />
            </label>
            <label>
              Altura
              <input
                value={height}
                onChange={event => setHeight(event.target.value)}
              />
            </label>
          </div>
        )}

        {type === 'image' && (
          <>
            <label>
              Legenda
              <input
                value={caption}
                onChange={event => setCaption(event.target.value)}
                placeholder="Se estiver vazia, será utilizado o título"
              />
            </label>
            <div className="row">
              <label>
                Fonte
                <input
                  value={source}
                  onChange={event => setSource(event.target.value)}
                />
              </label>
              <label>
                Texto alternativo
                <input
                  value={alternativeText}
                  onChange={event => setAlternativeText(event.target.value)}
                />
              </label>
            </div>
          </>
        )}

        <div className="actions">
          <button type="submit" disabled={loading || uploading}>
            {loading ? 'Salvando...' : 'Criar recurso'}
          </button>
        </div>
      </FormCard>
    </Container>
  );
};

export default CreateResource;
